// Turn a federation's `country` into a flag emoji. The column is free-text and inconsistent in
// practice — some rows hold an ISO-2 code ("HR"), others a full name ("Croatia") — so this accepts
// both: a 2-letter code maps straight to regional-indicator symbols; a known name is looked up
// first. Anything unrecognised returns '' so the caller can just omit the flag (never render junk).

// Full name → ISO-2, for the rows that store a human-readable country. Kept to the countries a
// referee federation is realistically in; extend as new tenants appear. Lower-cased keys so the
// lookup is case-insensitive.
const NAME_TO_ISO: Record<string, string> = {
  croatia: 'HR',
  germany: 'DE',
  france: 'FR',
  spain: 'ES',
  italy: 'IT',
  portugal: 'PT',
  slovenia: 'SI',
  serbia: 'RS',
  'bosnia and herzegovina': 'BA',
  hungary: 'HU',
  austria: 'AT',
  switzerland: 'CH',
  poland: 'PL',
  denmark: 'DK',
  sweden: 'SE',
  norway: 'NO',
  finland: 'FI',
  netherlands: 'NL',
  belgium: 'BE',
  'north macedonia': 'MK',
  montenegro: 'ME',
  romania: 'RO',
  'czech republic': 'CZ',
  czechia: 'CZ',
  slovakia: 'SK',
  greece: 'GR',
  iceland: 'IS',
  'united kingdom': 'GB',
  'united states': 'US',
  usa: 'US',
};

// Convert a validated ISO-2 code to its flag emoji by offsetting each letter into the Unicode
// regional-indicator block (A → 🇦, etc). Two of those symbols render as one flag glyph.
function isoToFlag(iso: string): string {
  const base = 0x1f1e6; // regional indicator 'A'
  return [...iso.toUpperCase()].map((c) => String.fromCodePoint(base + (c.charCodeAt(0) - 65))).join('');
}

export function countryFlag(country: string | null | undefined): string {
  if (!country) return '';
  const trimmed = country.trim();
  // Already an ISO-2 code (two ASCII letters) → straight to a flag.
  if (/^[A-Za-z]{2}$/.test(trimmed)) return isoToFlag(trimmed);
  // Otherwise treat it as a name and look it up; unknown → no flag rather than a wrong one.
  const iso = NAME_TO_ISO[trimmed.toLowerCase()];
  return iso ? isoToFlag(iso) : '';
}
